/* eslint-disable array-callback-return */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getFilters } from "../../services/FiltersService";
import ConfigFiltersSchema from "../../schema/ConfigFiltersSchema.json";
import MarketFiltersSchema from "../../schema/MarketFiltersSchema.json";
import OrganizationFiltersSchema from "../../schema/OrganizationFiltersSchema.json";
import ExportFiltersSchema from "../../schema/ExportFiltersSchema.json";
import { ControlType, FilterType, HttpCodes } from "../../constants/app.constant";
import { getReportOverview } from "../../services/ReportOverviewService";
import { DefaultFilterValues, FilterNames } from "../../constants/Filters";
import { upperCase } from "lodash";
import i18n from "i18next";

interface FilterApiPayload {
  data: any;
  module: string;
  reportId: string;
}
interface ReportOverviewApiPayload {
  data: any;
  reportName: string;
}

export interface State {
  filters: {
    config: any[];
    market: any[];
    organization: any[];
    export: any[];
    error: {
      config: any;
      market: any;
      organization: any;
      export: any;
    };
  };
  isFiltersLoading: boolean;
  isReportOverviewLoading: boolean;
  reportOverview: any[];
  payloadMappings: any;
  filterPayloads: any;
  activeFIlterName: any;
  activetabindex: number;
  selectedFilterToBePersisted: {
    config: any[];
    market: any[];
    organization: any[];
    export: any[];
  };
}

const initialState: State = {
  filters: {
    config: [],
    market: [],
    organization: [],
    export: [],
    error: {
      config: {
        isEmpty: false,
        isFailed: false
      },
      market: {
        isEmpty: false,
        isFailed: false
      },
      organization: {
        isEmpty: false,
        isFailed: false
      },
      export: {
        isEmpty: false,
        isFailed: false
      }
    }
  },
  isFiltersLoading: false,
  isReportOverviewLoading: false,
  reportOverview: [],
  payloadMappings: {
    config: {},
    market: {},
    organization: {},
    export: {}
  },
  filterPayloads: {
    countryCode: DefaultFilterValues.countryCode,
    language: DefaultFilterValues.language,
    languageKey: DefaultFilterValues.languageKey,
    geographyKey: DefaultFilterValues.geographyKey,
    clientPaySubmissionKey: DefaultFilterValues.clientPaySubmissionKey,
    clientData: DefaultFilterValues?.clientData,
    includeClientPaySubmissionKey:
      DefaultFilterValues.includeClientPaySubmissionKey,
    allClientPaySubmissionKey: DefaultFilterValues.allClientPaySubmissionKey,
    marketDefinitionKey: DefaultFilterValues.marketDefinitionKey,
    gradeSetId: DefaultFilterValues.gradeSetId,
    peerGroupId: DefaultFilterValues.peerGroupId,
    grade: DefaultFilterValues.grade,
    benchmarkingApproach: DefaultFilterValues.benchmarkingApproach,
    marketYear: DefaultFilterValues.marketYear,
    marketClassKey: DefaultFilterValues.marketClassKey,
    marketCompElementKey: DefaultFilterValues.marketCompElementKey,
    marketIndustryKey: DefaultFilterValues.marketIndustryKey,
    marketRegionKey: DefaultFilterValues.marketRegionKey,
    marketSubRegionKey: DefaultFilterValues.marketSubRegionKey,
    marketJobFamilyKey: DefaultFilterValues.marketJobFamilyKey,
    marketJobSubFamilyKey: DefaultFilterValues.marketJobSubFamilyKey,
    marketEmployeeCountKey: DefaultFilterValues.marketEmployeeCountKey,
    marketOwnershipTypeKey: DefaultFilterValues.marketOwnershipTypeKey,
    marketPosition: DefaultFilterValues.marketPosition,
    marketRevenueRangeKey: DefaultFilterValues.marketRevenueRangeKey,
    marketJobSkillKey: DefaultFilterValues.marketJobSkillKey,
    marketEducationKey: DefaultFilterValues.marketEducationKey,
    marketJobEducationKey: DefaultFilterValues.marketJobEducationKey,
    marketJointCommitteeKey: DefaultFilterValues.marketJointCommitteeKey,
    orgClassKey: DefaultFilterValues.orgClassKey,
    orgCompElementKey: DefaultFilterValues.orgCompElementKey,
    orgRegionKey: DefaultFilterValues.orgRegionKey,
    orgSubRegionKey: DefaultFilterValues.orgSubRegionKey,
    orgJobFamilyKey: DefaultFilterValues.orgJobFamilyKey,
    orgJobSubFamilyKey: DefaultFilterValues.orgJobSubFamilyKey,
    orgJobSkillKey: DefaultFilterValues.orgJobSkillKey,
    orgEducationKey: DefaultFilterValues.orgEducationKey,
    orgJobEducationKey: DefaultFilterValues.orgJobEducationKey,
    orgJointCommitteeKey: DefaultFilterValues.orgJointCommitteeKey,
    orgClientTypeKey: DefaultFilterValues.orgClientTypeKey,
    pointsMin: DefaultFilterValues.pointsMin,
    pointsMax: DefaultFilterValues.pointsMax,
    PercentileOrder: DefaultFilterValues.PercentileOrder,
    RaisePay: DefaultFilterValues.RaisePay,
    RaiseMarket: DefaultFilterValues.RaiseMarket,
    ReportType: DefaultFilterValues.ReportType,
    MinimumBroadening: DefaultFilterValues.MinimumBroadening,
    ReportOnColumns: DefaultFilterValues.ReportOnColumns,
    targetRangeMax: DefaultFilterValues.targetRangeMax,
    targetRangeMin: DefaultFilterValues.targetRangeMin,
    SelFilCategoryName: DefaultFilterValues.SelFilCategoryName
  },
  activeFIlterName: "",
  activetabindex: 0,
  selectedFilterToBePersisted: {
    config: [],
    market: [],
    organization: [],
    export: []
  }
};

type FilterModuleName = "market" | "organization" | "config" | "export";

const getModuleName = (module = ""): FilterModuleName => {
  if (module === FilterType.CONFIG) {
    return "config";
  }
  if (module === FilterType.MARKET) {
    return "market";
  }
  if (module === FilterType.ORGANIZATION) {
    return "organization";
  }
  return "export";
};

const getFilterSchema = (module = "") => {
  if (module === FilterType.CONFIG) {
    return ConfigFiltersSchema as any;
  }
  if (module === FilterType.MARKET) {
    return MarketFiltersSchema as any;
  }
  if (module === FilterType.ORGANIZATION) {
    return OrganizationFiltersSchema as any;
  }
  return ExportFiltersSchema as any;
};

export const getFiltersThunk = createAsyncThunk(
  "rewradBenchMark/filters",
  async (payload: FilterApiPayload, thunkApi) => {
    try {
      const response = await getFilters(
        payload.data,
        payload.module,
        payload.reportId
      );
      if (response && response?.responseCode === 200) {
        return {
          module: payload.module,
          data: response.data || [],
          responseCode: response.responseCode,
          isEmpty: response.data && response.data.length > 0 ? false : true,
          isFailed: false
        };
      } else if (response && response?.responseCode === 204) {
        return {
          module: payload.module,
          data: [],
          responseCode: response.responseCode,
          isEmpty: true,
          isFailed: false
        };
      }
      return {
        module: payload.module,
        data: [],
        responseCode: response.responseCode,
        isEmpty: false,
        isFailed: true
      };
    } catch (e) {
      return thunkApi.rejectWithValue({
        e,
        module: payload.module,
        isEmpty: false,
        isFailed: true
      });
    }
  }
);
export const getReportOverviewThunk = createAsyncThunk(
  "rewradBenchMark/reportOverview",
  async (payload: ReportOverviewApiPayload, thunkApi) => {
    try {
      const response = await getReportOverview(payload);
      if (response.responseCode == HttpCodes.SUCCESS) {
        return {
          data: response.data || []
        };
      }
      return {
        data: []
      };
    } catch (e) {
      return thunkApi.rejectWithValue({
        e
      });
    }
  }
);

const updateSubFamilyData = (filter: any = {}, data: any = []) => {
  let options = filter?.options || [];
  const groupDataOptions =
    data.find((data: any) => data.name === FilterNames.jobFamily)?.options ||
    [];
  const totalOptions = groupDataOptions.length + options.length;
  options = options.map((option: any) => {
    const index = option.label.indexOf("-");
    const group = option.label.substring(0, index);
    const label = option.label.slice(index + 1);
    return {
      ...option,
      label,
      group
    };
  });
  for (const familyDataOption of groupDataOptions) {
    options = options.map((option: any) => {
      if (
        option.group &&
        typeof option.group === "string" &&
        familyDataOption.key === option.group
      ) {
        option.group = {
          ...familyDataOption
        };
      }
      return option;
    });
  }
  options = options.sort(
    (a: any, b: any) =>
      -(b?.group?.label || "").localeCompare(a?.group?.label || "")
  );
  if (options.length > 0) {
    const selectAllOption = {
      key: "All",
      label: i18n.t("pa.all"),
      group: null
    };
    options.unshift(selectAllOption);
  }
  return {
    ...filter,
    options,
    totalOptions
  };
};

const updateFilterOptionAllKey = (filters: any = []) => {
  const _filters = filters.map((filter: any) => {
    if (filter?.controlType === ControlType.MULTI_SELECT) {
      const filteredOptions = filter.options.filter((option: any) => {
        return upperCase(option.label) !== "ALL";
      });
      if (filteredOptions.length > 0) {
        const selectAllOption = {
          key: "All",
          label: i18n.t("pa.all")
        };
        filteredOptions.unshift(selectAllOption);
      }
      filter.options = filteredOptions;
      return filter;
    }
    return filter;
  });
  return _filters;
};

export const configSlice = createSlice({
  name: "filterSlice",
  initialState,
  reducers: {
    resetState: () => initialState,
    initPayloadMappings: (state) => {
      ConfigFiltersSchema.map((filter) => {
        state.payloadMappings.config[filter.name] =
          filter.payloadKey ?? filter.name;
      });
      MarketFiltersSchema.map((filter) => {
        state.payloadMappings.market[filter.name] =
          filter.payloadKey ?? filter.name;
      });
      OrganizationFiltersSchema.map((filter) => {
        state.payloadMappings.organization[filter.name] =
          filter.payloadKey ?? filter.name;
      });
      ExportFiltersSchema.map((filter) => {
        state.payloadMappings.export[filter.name] =
          filter.payloadKey ?? filter.name;
      });
    },
    resetFilterDataOptions: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
    },
    resetFilterPayloads: (state) => {
      state.filterPayloads = { ...initialState.filterPayloads };
    },
    resetFilterErrorData: (state, action) => {
      state.filters.error = {
        ...state.filters.error,
        ...action.payload
      };
    },
    setFilterPayloads: (state, action) => {
      state.filterPayloads = { ...action.payload };
    },
    updateConfigFilter: (state, action) => {
      state.filters.config = action.payload;
    },
    setActiveFilter: (state, action) => {
      state.activeFIlterName = action.payload;
    },
    setTabIndex: (state, action) => {
      state.activetabindex = action.payload;
    },
    setSelectedFilterToBePersisted: (state, action) => {
      if (action.payload.filterType === FilterType.CONFIG) {
        state.selectedFilterToBePersisted.config =
          action.payload.selectedFilters;
      } else if (action.payload.filterType === FilterType.MARKET) {
        state.selectedFilterToBePersisted.market =
          action.payload.selectedFilters;
      } else if (action.payload.filterType === FilterType.ORGANIZATION) {
        state.selectedFilterToBePersisted.organization =
          action.payload.selectedFilters;
      } else if (action.payload.filterType === FilterType.EXPORT) {
        state.selectedFilterToBePersisted.export =
          action.payload.selectedFilters;
      } else {
        state.selectedFilterToBePersisted = {
          config: [],
          market: [],
          organization: [],
          export: []
        };
      }
    }
  },

  extraReducers: (builder) => {
    builder.addCase(getFiltersThunk.pending, (state, action) => {
      state.isFiltersLoading = true;
    });
    builder.addCase(getFiltersThunk.fulfilled, (state, action) => {
      state.isFiltersLoading = false;
      let filters = action.payload.data.map(
        (filter: any, index: number, data: any) => {
          if (filter.name === FilterNames.jobSubFamily) {
            filter = updateSubFamilyData(filter, data);
          }
          const filterScehma = getFilterSchema(action.payload.module).find(
            (schema: any) => schema.name === filter.name
          );
          if (filterScehma && !filterScehma.isHideFilter) {
            return {
              ...filterScehma,
              ...filter
            };
          }
          return null;
        }
      );

      filters = updateFilterOptionAllKey(filters);
      // Remove the empty options filter for multiple select / single select
      filters = filters.filter((filter: any) => {
        if (
          filter &&
          filter.controlType !== ControlType.MULTI_SELECT &&
          filter.controlType !== ControlType.SINGLE_SELECT
        ) {
          return true;
        } else if (filter && filter.options.length) {
          return true;
        }
        return false;
      });

      // Update the -1 to All for options

      if (action.payload.module === FilterType.CONFIG) {
        let configFilter = state.filters[getModuleName(FilterType.CONFIG)];
        state.filters[getModuleName(FilterType.CONFIG)] = [
          ...configFilter,
          ...filters
        ];
      } else {
        state.filters[getModuleName(action.payload.module)] = filters;
      }
      state.filters.error[getModuleName(action.payload.module)].isEmpty =
        action.payload.isEmpty;
      state.filters.error[getModuleName(action.payload.module)].isFailed =
        action.payload.isFailed;
    });
    builder.addCase(getFiltersThunk.rejected, (state, action: any) => {
      state.isFiltersLoading = false;
      state.filters[getModuleName(action.payload.module)] = [];
      state.filters.error[getModuleName(action.payload.module)].isEmpty =
        action.payload.isEmpty;
      state.filters.error[getModuleName(action.payload.module)].isFailed =
        action.payload.isFailed;
    });

    builder.addCase(getReportOverviewThunk.pending, (state, action) => {
      state.isReportOverviewLoading = true;
    });
    builder.addCase(getReportOverviewThunk.fulfilled, (state, action) => {
      state.isReportOverviewLoading = false;
      state.reportOverview = action.payload.data;
    });
    builder.addCase(getReportOverviewThunk.rejected, (state, action: any) => {
      state.isReportOverviewLoading = false;
      state.reportOverview = [];
    });
  }
});

export const {
  resetState,
  resetFilterDataOptions,
  resetFilterPayloads,
  resetFilterErrorData,
  initPayloadMappings,
  updateConfigFilter,
  setFilterPayloads,
  setActiveFilter,
  setTabIndex,
  setSelectedFilterToBePersisted
} = configSlice.actions;

export default configSlice.reducer;
